import * as turf from '@turf/turf';
import { AnchorLayerType, LayerIdEnum } from '@/utils/constant';
import {
  cloneCoordinates,
  createGeometry,
  extractOpacity,
  toBdPoint,
} from './geometry';
import { getPolygonArea } from './geoUtils';
import {
  getUnclosedPolygonPoints,
  shouldEnablePolygonParse,
} from './polygonParsePolicy';
import { getAttributeIcon } from '@/utils/attributeIcon';
import {
  returnNormalAnchorFillColor,
  returnSelectedAnchorFillColor,
  returnAnchorBorderColor,
} from './styles';
import { LayerRegistry } from './layerRegistry';
import type { FeatureLike, GeometryType } from './types';

export interface OverlayHost {
  options: any;
  isDrawing: () => boolean;
  changeMapCenter: (coordinates: any) => void;
}

const SMALL_POLYGON_HIT_AREA_SQM = 1;
const SMALL_POLYGON_HIT_SIZE_PX = 18;
// 命中辅助覆盖物只用于扩大点击热区，不能参与视觉渲染。
// 之前使用 0.01 透明度在缩放动画中仍可能被肉眼看到，表现为小面周围“抖动/闪动”。
const TRANSPARENT_HIT_OPACITY = 0;
const TRANSPARENT_HIT_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="${SMALL_POLYGON_HIT_SIZE_PX}" height="${SMALL_POLYGON_HIT_SIZE_PX}" viewBox="0 0 ${SMALL_POLYGON_HIT_SIZE_PX} ${SMALL_POLYGON_HIT_SIZE_PX}"><rect width="${SMALL_POLYGON_HIT_SIZE_PX}" height="${SMALL_POLYGON_HIT_SIZE_PX}" fill="#fff" fill-opacity="${TRANSPARENT_HIT_OPACITY}"/></svg>`
)}`;


export class OverlayManager {
  selectedPolygon: FeatureLike | null = null;

  constructor(
    private map: any,
    private layers: LayerRegistry,
    private host: OverlayHost
  ) {}

  private applyOverlayStyle = (feature: FeatureLike, style: any) => {
    feature.style = style;
    if (!feature.overlay) {
      return;
    }
    const fillColor = style?.fill?.color;
    const strokeColor = style?.stroke?.color;
    const strokeWeight = style?.stroke?.width;
    const lineDash = style?.stroke?.lineDash;
    const icon = style?.image;

    if (feature.geometryType === 'Polygon') {
      feature.overlay.setFillColor?.(fillColor);
      feature.overlay.setFillOpacity?.(extractOpacity(fillColor));
      feature.overlay.setStrokeColor?.(strokeColor);
      feature.overlay.setStrokeWeight?.(strokeWeight ?? 1);
      feature.overlay.setStrokeOpacity?.(extractOpacity(strokeColor));
      feature.overlay.setStrokeStyle?.(
        Array.isArray(lineDash) && lineDash.length > 0 ? 'dashed' : 'solid'
      );
    }
    if (feature.geometryType === 'LineString') {
      feature.overlay.setStrokeColor?.(strokeColor);
      feature.overlay.setStrokeWeight?.(strokeWeight ?? 1);
      feature.overlay.setStrokeOpacity?.(extractOpacity(strokeColor));
      feature.overlay.setStrokeStyle?.(
        Array.isArray(lineDash) && lineDash.length > 0 ? 'dashed' : 'solid'
      );
    }
    if (feature.geometryType === 'Point' && icon?.src) {
      const iconSize = icon.imageSize || { width: 32, height: 32 };
      const imageSize = new window.BMapGL.Size(iconSize.width, iconSize.height);
      const anchor = icon.anchor
        ? new window.BMapGL.Size(
          Math.round(icon.anchor[0] * iconSize.width),
          Math.round(icon.anchor[1] * iconSize.height)
        )
        : new window.BMapGL.Size(iconSize.width / 2, iconSize.height);
      feature.overlay.setIcon?.(
        new window.BMapGL.Icon(icon.src, imageSize, {
          imageSize,
          anchor,
        })
      );
      if (typeof icon.scale === 'number') {
        feature.overlay.setOffset?.(
          new window.BMapGL.Size(0, Math.round((1 - icon.scale) * 8))
        );
      }
    }
  };

  private createFeature = ({
    id,
    geometryType,
    coordinates,
    rawCoordinates,
    overlay,
    auxiliaryOverlays,
    layerId,
    style,
    data,
  }: {
    id: string | number;
    geometryType: GeometryType;
    coordinates: any;
    rawCoordinates?: any;
    overlay: any;
    auxiliaryOverlays?: any[];
    layerId: string;
    style?: any;
    data?: any;
  }): FeatureLike => {
    const feature: FeatureLike = {
      id,
      geometryType,
      coordinates: cloneCoordinates(coordinates),
      rawCoordinates: cloneCoordinates(rawCoordinates ?? coordinates),
      data,
      overlay,
      auxiliaryOverlays,
      layerId,
      style: style ?? {},
      setStyle: (nextStyle: any) => {
        this.applyOverlayStyle(feature, nextStyle);
      },
      setGeometry: (geometry: any) => {
        const nextCoordinates = geometry?.getCoordinates
          ? geometry.getCoordinates()
          : geometry;
        feature.coordinates = cloneCoordinates(nextCoordinates);
        feature.rawCoordinates = cloneCoordinates(nextCoordinates);
        if (geometryType === 'Point') {
          feature.overlay.setPosition?.(toBdPoint(nextCoordinates));
        }
      },
      getGeometry: () => createGeometry(geometryType, feature.coordinates),
      getId: () => feature.id,
    };
    this.applyOverlayStyle(feature, feature.style);
    return feature;
  };

  clearSelection = () => {
    this.selectedPolygon = null;
  };

  private createSmallPolygonHitOverlays(
    points: [number, number][],
    onClick: () => void
  ) {
    const unclosedPoints = getUnclosedPolygonPoints(points);
    if (
      unclosedPoints.length < 3 ||
      getPolygonArea(unclosedPoints) > SMALL_POLYGON_HIT_AREA_SQM
    ) {
      return [];
    }

    const closedPoints = [...unclosedPoints, unclosedPoints[0]];
    const center = turf.centroid(turf.polygon([closedPoints])).geometry.coordinates as [number, number];
    const hitLine = new window.BMapGL.Polyline(closedPoints.map((point) => toBdPoint(point)), {
      strokeColor: '#fff',
      strokeOpacity: TRANSPARENT_HIT_OPACITY,
      strokeWeight: SMALL_POLYGON_HIT_SIZE_PX,
      enableMassClear: false,
      enableClicking: true,
      enableParse: false,
    });
    hitLine.addEventListener?.('click', onClick);
    const iconSize = new window.BMapGL.Size(
      SMALL_POLYGON_HIT_SIZE_PX,
      SMALL_POLYGON_HIT_SIZE_PX
    );
    const hitOverlay = new window.BMapGL.Marker(toBdPoint(center), {
      icon: new window.BMapGL.Icon(TRANSPARENT_HIT_ICON, iconSize, {
        imageSize: iconSize,
        anchor: new window.BMapGL.Size(
          SMALL_POLYGON_HIT_SIZE_PX / 2,
          SMALL_POLYGON_HIT_SIZE_PX / 2
        ),
      }),
      enableMassClear: false,
      enableClicking: true,
    });
    hitOverlay.addEventListener?.('click', onClick);
    return [hitLine, hitOverlay];
  }

  private handlePolygonClick(feature: FeatureLike) {
    if (this.host.isDrawing()) {
      return;
    }
    if (!this.selectedPolygon) {
      this.selectedPolygon = feature;
      this.host.options.selectCallBack?.(feature, feature.getId(), 'focus');
      return;
    }
    const selectedPolygonId = this.selectedPolygon.getId();
    this.host.options.selectCallBack?.(
      this.selectedPolygon,
      selectedPolygonId,
      'blur'
    );
    if (selectedPolygonId === feature.getId()) {
      this.selectedPolygon = null;
      return;
    }
    this.selectedPolygon = feature;
    this.host.options.selectCallBack?.(feature, feature.getId(), 'focus');
  }

  createLine(
    coordinates: any,
    style: any,
    id?: string | number,
    layerId: string = LayerIdEnum.LANE_LINE_LAYER
  ) {
    const bdPoints = coordinates.map((point: [number, number]) =>
      toBdPoint(point)
    );
    // enableParse 默认保持 SDK 行为（true，按级别抽稀）。
    // 米级小图形（如车辆朝向线/基准线）必须传 false，否则缩放重投影时线会抖动/失真。
    const overlay = new window.BMapGL.Polyline(bdPoints, {
      coordType: 'BMAP_COORD_GCJ02',
      enableParse: style?.enableParse ?? true,
    });
    return this.createFeature({
      id: id ?? `line${this.layers.lines.length + 1}`,
      geometryType: 'LineString',
      coordinates,
      overlay,
      layerId,
      style: {
        stroke: {
          color: style?.color,
          width: style?.width,
          lineDash: style?.lineDash,
        },
      },
    });
  }

  drawPolygon = (
    id: string | number,
    points: any,
    type: AnchorLayerType,
    val: any
  ) => {
    // BMapGL.Polygon 不需要首尾重复闭合点；去掉闭合重复点，避免零长度边在缩放重投影时造成渲染抖动。
    const renderPoints = getUnclosedPolygonPoints(points);
    const polygonPoints = renderPoints.map((point: [number, number]) =>
      toBdPoint(point)
    );
    const fillColor = returnNormalAnchorFillColor(type, val.subType);
    const strokeColor = returnSelectedAnchorFillColor(type, val.subType);
    const strokeWeight = 2;
    const enableParse = shouldEnablePolygonParse(renderPoints);

    // 创建 Polygon 时直接传入完整样式选项：
    // 1. 避免先创建空对象再 setStyle，部分机型/缩放级别下样式可能不生效
    // 2. 禁止 massClear 避免被地图 clearOverlays 误清除
    // 3. 严格控制 enableParse：小面、短边面、低点数面关闭，只有复杂大面才开启
    const overlay = new window.BMapGL.Polygon(polygonPoints, {
      fillColor,
      fillOpacity: extractOpacity(fillColor),
      strokeColor,
      strokeOpacity: extractOpacity(strokeColor),
      strokeWeight,
      strokeStyle: 'solid',
      enableMassClear: false,
      enableClicking: true,
      enableParse,
    });
    const onPolygonClick = () => this.handlePolygonClick(feature);
    const auxiliaryOverlays = this.createSmallPolygonHitOverlays(
      points,
      onPolygonClick
    );
    const feature = this.createFeature({
      id: id === undefined || id === null
        ? `popOverlay${this.layers.polygons.length + 1}`
        : id,
      geometryType: 'Polygon',
      coordinates: points,
      overlay,
      auxiliaryOverlays: auxiliaryOverlays.length > 0 ? auxiliaryOverlays : undefined,
      layerId: LayerIdEnum.ANCHOR_LAYER,
      data: val,
      style: {
        fill: {
          color: fillColor,
        },
        stroke: {
          color: strokeColor,
          width: strokeWeight,
        },
      },
    });
    overlay.addEventListener?.('click', onPolygonClick);
    this.layers.ensureLayer(LayerIdEnum.ANCHOR_LAYER).addFeature(
      this.layers.registerFeature(feature)
    );

    if (type === AnchorLayerType.ATTRIBUTE_ANNOTATION && val.subType) {
      const iconSrc = getAttributeIcon(val.subType, 'map');
      if (iconSrc) {
        const polygon = turf.polygon([points]);
        const center = turf.centroid(polygon).geometry.coordinates;
        const centerPoint = this.createPointFeature(
          center,
          iconSrc,
          'attribute' + id,
          {
            scale: 0.4,
            imageSize: { width: 32, height: 32 },
          }
        );
        this.layers.ensureLayer(LayerIdEnum.ATTRIBUTE_MARK_LAYER)
          .getSource()
          .addFeature(centerPoint);
      }
    }
  };

  deletePolygon(id: number | string) {
    if (this.selectedPolygon?.getId() === id) {
      this.selectedPolygon = null;
    }
    this.layers.ensureLayer(LayerIdEnum.ANCHOR_LAYER).removeFeature(id);
    this.layers.getLayer(LayerIdEnum.ATTRIBUTE_MARK_LAYER)?.removeFeature(
      `attribute${id}`
    );
    this.layers.unregisterFeature(id, `attribute${id}`);
  }

  /** 应用多边形普通态样式 */
  private applyNormalStyle(feature: FeatureLike) {
    const data = feature.data;
    feature.setStyle({
      fill: { color: returnNormalAnchorFillColor(data?.type, data?.subType) },
      stroke: { color: returnAnchorBorderColor(data?.type, data?.subType), width: 2 },
    });
  }

  /** 应用多边形选中态样式 */
  private applySelectedStyle(feature: FeatureLike) {
    const data = feature.data;
    feature.setStyle({
      fill: { color: returnSelectedAnchorFillColor(data?.type, data?.subType) },
      stroke: { color: returnAnchorBorderColor(data?.type, data?.subType), width: 2 },
    });
  }

  /**
   * 高亮指定多边形
   *
   * @param options.panTo 为 false 时只应用样式，不触发 selectCallBack，
   *                      用于 moveend 后程序恢复选中态（避免地图被拽回中心）
   * @returns 选中的 feature，未找到时返回 undefined
   */
  highLightPolygon = (
    id: number,
    options?: { panTo?: boolean }
  ): FeatureLike | undefined => {
    const shouldTriggerCallback = options?.panTo !== false;
    const selectedPolygonId = this.selectedPolygon?.getId() ?? null;

    // 取消旧选中态
    if (selectedPolygonId !== null && selectedPolygonId !== id) {
      if (shouldTriggerCallback) {
        this.host.options.selectCallBack?.(
          this.selectedPolygon,
          selectedPolygonId,
          'blur'
        );
      } else {
        this.selectedPolygon && this.applyNormalStyle(this.selectedPolygon);
      }
      this.selectedPolygon = null;
    }

    // 再次点击已选中面：取消选中
    if (id === selectedPolygonId) {
      if (shouldTriggerCallback) {
        this.host.options.selectCallBack?.(
          this.selectedPolygon,
          selectedPolygonId,
          'blur'
        );
      } else {
        this.selectedPolygon && this.applyNormalStyle(this.selectedPolygon);
      }
      this.selectedPolygon = null;
      return undefined;
    }

    // 设置新选中态
    const polygon = this.layers.polygons.filter((item: any) => item.getId() === id)[0];
    if (id !== undefined && polygon) {
      if (shouldTriggerCallback) {
        // 手动点击路径：触发 selectCallBack，由 ViewStrategy.onSelect 处理平移/弹窗/lane 线
        this.host.options.selectCallBack(polygon, id, 'focus');
      } else {
        // 程序恢复路径：直接应用高亮样式，不触发业务回调
        this.applySelectedStyle(polygon);
      }
      this.selectedPolygon = polygon;
      return polygon;
    }

    return undefined;
  };

  createPointFeature = (
    coordinates: any,
    imgSrc: string,
    pointId?: string,
    otherStyle?: any,
    anchor?: any,
    layerId: string = LayerIdEnum.SEARCHPOINT_LAYER
  ) => {
    const overlay = new window.BMapGL.Marker(toBdPoint(coordinates), {
      coordType: 'BMAP_COORD_GCJ02',
    });
    const imageSize = otherStyle?.imageSize ?? { width: 32, height: 32 };
    const feature = this.createFeature({
      id: pointId === undefined ? `point${this.layers.points.length + 1}` : pointId,
      geometryType: 'Point',
      coordinates,
      overlay,
      layerId,
      style: {
        image: {
          ...otherStyle,
          src: imgSrc,
          imageSize,
          anchor: anchor ?? [0.5, 1],
        },
      },
    });
    this.layers.registerFeature(feature);
    return feature;
  };

  exportGeometry = (feature: FeatureLike) => {
    const coordinates = feature.getGeometry().getCoordinates();
    return {
      type: 'Polygon',
      coordinates: [coordinates],
    };
  };
}
